<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  getValidations,
  Validation,
  updateStateValidation
} from '@/app/core/services/validations'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableValidation = ref(null)
const validationUpdateState = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('validations.list.code'), table: 'code' },
  { name: 'name', title: t('validations.list.name'), table: 'name' },
  {
    name: 'listCountries',
    title: t('validations.list.countries'),
    table: 'countries_name'
  },
  {
    name: 'description',
    title: t('validations.list.descriptionList'),
    table: 'description'
  },
  { name: 'state', title: t('validations.list.status'), table: 'is_inactive' },
  {
    name: 'updated_at',
    title: t('validations.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<Validation[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createValidation = () => {
  void router.push({ name: 'CreateValidation' })
}
const updateValidation = (id: any) => {
  void router.push({ name: 'UpdateValidation', params: { id } })
}
const getValidation = (id: any) => {
  void router.push({ name: 'GetValidation', params: { id } })
}

/* Cambiar estado de la validacion guardada */
const changeStateValidation = (values: any) => {
  typeAlertModal.value = 'question'
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  validationUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  titleModal.value = t('validations.list.actionStatus', {
    status: text,
    validation: values.name as string
  })
  descriptionModal.value = ''
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  showModal.value = true
}
const changeStateConfirm = async () => {
  showModal.value = false
  await updateStateValidation(
    validationUpdateState.value[0].id,
    validationUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: !validationUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('validations.notification.changeStatus', {
          location: validationUpdateState.value[0].name as string,
          status: !validationUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void validations()
    })
    .catch((error: any) => {
      notificationError(error, 'validations', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar validaciones */
const validations = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getValidations(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'validations', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="changeStateConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('validations.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('validations.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="validations" />
          <DBtn @click.prevent="validations" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createValidation()"
          v-if="store.hasPermission('validation:create')">
          {{
            t('form.button.create', { form: t('validations.list.title', 0) })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableValidation"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), validations()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), validations()">
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #listCountries="data: any">
        <div class="flex items-center">
          <div
            v-for="(country, index) in data.countries_name
              .split(',')
              .slice(0, 1)"
            :key="index">
            {{ country }}
          </div>
          <div
            v-if="data.countries_name.split(',').length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.countries_name.split(',').length - 1 }}+
          </div>
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('validation:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getValidation(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('validation:update') && !data.is_inactive"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateValidation(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('validation:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('validations.list.activate')
                : t('validations.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateValidation(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
